import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import { AlertTriangle, CheckCircle2, Clock, Loader2, Send } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import type {
  PublicApplicantAttemptResponse,
  PublicApplyMeResponse,
  PublicSubmitAttemptResponse,
} from "@eduviaos/shared/types";
import {
  fetchApplyAttempt,
  saveApplyResponse,
  startApplyAttempt,
  submitApplyAttempt,
} from "@/lib/api";
import { assessmentLabel } from "@/lib/assessment-label";
import { parsePublicError } from "@/lib/error-utils";
import { formatDateTime, formatTimeUntil } from "@/lib/format-time";
import { createProctorClient, isProctorProfileActive } from "@/lib/proctor-client";
import { useProctorLifecycle } from "@/hooks/useProctorLifecycle";
import { useScreenCaptureLoop } from "@/hooks/useScreenCaptureLoop";
import { useWebcamStillCapture } from "@/hooks/useWebcamStillCapture";
import { PageCard } from "@/components/AppFrame";
import { ErrorState } from "@/components/ErrorState";
import { IdDocumentCapture } from "@/components/apply/IdDocumentCapture";
import { ProctorConsentGate } from "@/components/apply/ProctorConsentGate";
import { QuestionRenderer } from "@/components/apply/QuestionRenderer";
import { ScreenCaptureGate } from "@/components/apply/ScreenCaptureGate";

/**
 * Phase F4 BE-3 + F10 — applicant-facing assessment runner.
 *
 * Slim slice (full F10 FE taker — visual LaTeX editor, image upload UX,
 * proctoring camera/audio/screen capture, ML cheat-detection telemetry —
 * lives in subsequent slices). What this PR delivers:
 *   - Discovery + start (POST :start when status='assigned')
 *   - One-question-at-a-time UI with prev/next + jump-to grid
 *   - Autosave on response change (debounced 600ms; immediate on
 *     navigation away from the question)
 *   - Final submit with confirmation + post-submit summary
 *   - Question-type coverage: mcq_single, mcq_multi, yes_no,
 *     true_false, short_answer, latex_equation, numeric,
 *     match_following, fill_in_the_blank, long_answer, code,
 *     file_upload (graceful "answer outside the portal" copy)
 *
 * Token semantics: single-use; the BE consumes it ONLY on submit. Save
 * + start are idempotent so a parent who reopens the link mid-attempt
 * sees their saved progress.
 */
export function ApplyAttemptSection({
  token,
}: {
  token: string;
  // `me` is currently unused but kept on the prop type for symmetry with
  // the form/status sections — the assessment runner derives expiry
  // from `attempt.expires_at` (which is the truth from the BE) rather
  // than `me.action_authorised.expires_at` (token-level), since the
  // attempt window can be shorter than the token TTL.
  me: PublicApplyMeResponse;
}) {
  const tenant = React.useMemo(() => getCurrentTenant(), []);
  const tenantSlug = tenant.slug ?? "";
  const queryClient = useQueryClient();

  const attemptQuery = useQuery({
    queryKey: ["public-apply-attempt", tenantSlug, token],
    queryFn: () => fetchApplyAttempt(token),
    enabled: !!tenantSlug && !!token,
    // Don't auto-refetch; the parent's local saves are the only source
    // of mutation while the page is open. Refetching would clobber
    // unsaved edits in the active question editor.
    staleTime: Infinity,
  });

  const startMutation = useMutation({
    mutationFn: () => startApplyAttempt(token),
    onSuccess: (data) => {
      queryClient.setQueryData(["public-apply-attempt", tenantSlug, token], data);
    },
  });

  const [submitState, setSubmitState] = React.useState<PublicSubmitAttemptResponse | null>(null);
  // F11: gate the StartGate behind a consent step when the proctor
  // profile is active. The applicant must grant camera + mic
  // permissions before they can hit "Start assessment". State is local
  // (not in the BE attempt row) — re-mounting the page resets it,
  // which is the correct behaviour: the BE has the persisted
  // ``consent_granted`` event in either case.
  const [proctorConsentGiven, setProctorConsentGiven] = React.useState(false);
  // F11 FE-2a: gate the StartGate further behind a one-shot ID document
  // capture step. Same lifetime semantics as `proctorConsentGiven` —
  // local state, no BE persistence flag, the BE row stores the
  // ``id_document`` ProctorArtifact in either case.
  const [proctorIdCaptured, setProctorIdCaptured] = React.useState(false);
  // F11 FE-2b2: the display-media stream produced by ScreenCaptureGate.
  // Held here (not in the gate) so the screen-capture loop can outlive
  // the gate component, and so we can stop the tracks at attempt-end
  // without leaking the GPU compositor pipeline.
  const [screenStream, setScreenStream] = React.useState<MediaStream | null>(null);

  React.useEffect(() => {
    return () => {
      if (screenStream) {
        for (const track of screenStream.getTracks()) track.stop();
      }
    };
  }, [screenStream]);

  // The proctor client is bound to the apply token for the attempt's
  // lifetime. ``useMemo`` so the same instance is reused across renders
  // — the lifecycle hook's listener attach/detach cycle is keyed on it.
  const proctorClient = React.useMemo(() => createProctorClient(token), [token]);
  const proctorActive = isProctorProfileActive(attemptQuery.data?.proctor_profile);

  // Wire DOM lifecycle events to the BE only after consent has been
  // granted AND the runner is actually live. Before consent, we have
  // no BE-side audit trail to attach the events to (and the user
  // hasn't agreed yet).
  useProctorLifecycle({
    client: proctorClient,
    enabled: proctorActive && proctorConsentGiven && attemptQuery.data?.status === "in_progress",
  });

  // F11 FE-2b1: periodic webcam-still capture loop. Engaged ONLY
  // while the runner is actually live AND the proctor profile is
  // active AND the applicant has both consented and uploaded their
  // ID document — earlier states have nothing to capture (and the
  // F12 ML model wouldn't have an ID baseline to compare against).
  useWebcamStillCapture({
    client: proctorClient,
    enabled:
      proctorActive &&
      proctorConsentGiven &&
      proctorIdCaptured &&
      attemptQuery.data?.status === "in_progress",
  });

  // F11 FE-2b2: periodic screen-capture loop. Engages while the runner
  // is in_progress AND the share gate has produced a stream. We don't
  // gate on `proctorIdCaptured` here because both gates run before
  // the StartGate — by the time the runner is `in_progress` both will
  // have already passed. The hook is a no-op when the stream is null.
  useScreenCaptureLoop({
    client: proctorClient,
    stream: attemptQuery.data?.status === "in_progress" ? screenStream : null,
  });

  if (attemptQuery.isLoading) {
    return (
      <PageCard>
        <div
          role="status"
          aria-live="polite"
          className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
        >
          <Loader2 className="text-primary h-4 w-4 animate-spin" />
          <span>Loading assessment…</span>
        </div>
      </PageCard>
    );
  }

  if (attemptQuery.error) {
    return <AttemptError err={attemptQuery.error} />;
  }

  const attempt = attemptQuery.data;
  if (!attempt) return null;

  if (submitState) {
    return <SubmittedView submit={submitState} attempt={attempt} />;
  }

  if (attempt.status === "graded" || attempt.status === "submitted") {
    return <AlreadyDoneView attempt={attempt} />;
  }
  if (attempt.status === "withdrawn" || attempt.status === "expired") {
    return (
      <ErrorState
        title="Assessment closed"
        body={
          attempt.status === "expired"
            ? "This assessment window has closed. Please contact the institution if you still need to attempt it."
            : "This assessment was withdrawn by the institution."
        }
      />
    );
  }

  if (attempt.status === "assigned") {
    if (proctorActive && !proctorConsentGiven) {
      return (
        <ProctorConsentGate
          client={proctorClient}
          templateLabel={assessmentLabel(attempt)}
          onConsentGranted={() => setProctorConsentGiven(true)}
        />
      );
    }
    if (proctorActive && !proctorIdCaptured) {
      return (
        <IdDocumentCapture client={proctorClient} onIdCaptured={() => setProctorIdCaptured(true)} />
      );
    }
    if (proctorActive && screenStream === null) {
      return <ScreenCaptureGate client={proctorClient} onScreenStreamReady={setScreenStream} />;
    }
    return (
      <StartGate
        attempt={attempt}
        starting={startMutation.isPending}
        onStart={() => startMutation.mutate()}
        startError={startMutation.error}
      />
    );
  }

  // status === "in_progress" → render the runner
  return (
    <RunnerBody
      attempt={attempt}
      token={token}
      tenantSlug={tenantSlug}
      onSubmitted={(result) => setSubmitState(result)}
    />
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function StartGate({
  attempt,
  starting,
  onStart,
  startError,
}: {
  attempt: PublicApplicantAttemptResponse;
  starting: boolean;
  onStart: () => void;
  startError: unknown;
}) {
  const totalQuestions = attempt.items.length;
  const totalMarks = attempt.total_marks;
  const expiresIn = formatTimeUntil(attempt.expires_at);

  const errorMsg = startError ? parsePublicError(startError).message : null;

  return (
    <PageCard
      title="Ready to begin?"
      subtitle="Take a moment to read the instructions before you start. Your timer begins when you click Start."
    >
      <ul className="text-foreground/90 grid gap-2 text-sm">
        <li className="flex items-start gap-2">
          <span
            aria-hidden
            className="bg-primary/10 text-primary mt-0.5 h-5 w-5 flex-shrink-0 rounded-md text-center text-xs font-bold leading-5"
          >
            ?
          </span>
          <span>
            <span className="font-medium">{totalQuestions}</span> question
            {totalQuestions === 1 ? "" : "s"} · Total marks:{" "}
            <span className="font-medium">{totalMarks}</span>
          </span>
        </li>
        <li className="flex items-start gap-2">
          <Clock className="text-foreground/70 mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Your link closes in <span className="font-medium">{expiresIn}</span>. Once you submit
            you can't change your answers.
          </span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
          <span>
            Answers are saved automatically as you type. You can navigate freely between questions.
          </span>
        </li>
      </ul>

      {errorMsg && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mt-4 flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <Button type="button" onClick={onStart} disabled={starting} size="lg">
          {starting ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              Starting…
            </>
          ) : (
            "Start assessment"
          )}
        </Button>
      </div>
    </PageCard>
  );
}

function RunnerBody({
  attempt,
  token,
  tenantSlug,
  onSubmitted,
}: {
  attempt: PublicApplicantAttemptResponse;
  token: string;
  tenantSlug: string;
  onSubmitted: (result: PublicSubmitAttemptResponse) => void;
}) {
  const queryClient = useQueryClient();
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [confirmSubmit, setConfirmSubmit] = React.useState(false);

  // Local working copy of responses keyed by question_id so the
  // currently-edited question doesn't fight the autosave roundtrip.
  const [localResponses, setLocalResponses] = React.useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      attempt.items.flatMap((it) => (it.response !== null ? [[it.question_id, it.response]] : [])),
    ),
  );

  const saveMutation = useMutation({
    mutationFn: (body: { question_id: string; response: Record<string, unknown> }) =>
      saveApplyResponse(token, body),
    onSuccess: (data) => {
      queryClient.setQueryData(["public-apply-attempt", tenantSlug, token], data);
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => submitApplyAttempt(token),
    onSuccess: (data) => {
      onSubmitted(data);
    },
  });

  const items = attempt.items;
  const activeItem = items[activeIdx];

  // Debounced autosave per question.
  const lastSavedRef = React.useRef<Record<string, string>>({});
  React.useEffect(() => {
    if (!activeItem) return;
    const local = localResponses[activeItem.question_id];
    if (local === undefined) return;
    const serialised = stableStringify(local);
    if (lastSavedRef.current[activeItem.question_id] === serialised) return;

    const timer = window.setTimeout(() => {
      lastSavedRef.current[activeItem.question_id] = serialised;
      saveMutation.mutate({
        question_id: activeItem.question_id,
        response: toRecord(local),
      });
    }, 600);

    return () => window.clearTimeout(timer);
    // saveMutation is stable per render via TanStack — referencing it
    // here would only re-run the effect on every render and rebreak
    // debouncing. Same for queryClient. Trust the runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem?.question_id, localResponses[activeItem?.question_id ?? ""]]);

  const flushSaveAndNavigate = React.useCallback(
    (delta: number) => {
      // Flush the active question synchronously (best-effort) before
      // navigating so a quick "save → next" tap can't lose the answer.
      if (activeItem) {
        const local = localResponses[activeItem.question_id];
        if (local !== undefined) {
          const serialised = stableStringify(local);
          if (lastSavedRef.current[activeItem.question_id] !== serialised) {
            lastSavedRef.current[activeItem.question_id] = serialised;
            saveMutation.mutate({
              question_id: activeItem.question_id,
              response: toRecord(local),
            });
          }
        }
      }
      setActiveIdx((idx) => Math.max(0, Math.min(items.length - 1, idx + delta)));
    },
    [activeItem, items.length, localResponses, saveMutation],
  );

  const answeredCount = items.filter(
    (it) => localResponses[it.question_id] !== undefined || it.response !== null,
  ).length;

  if (!activeItem) {
    return (
      <ErrorState
        title="No questions found"
        body="The assessment is empty. Please contact the institution — this is a configuration error."
      />
    );
  }

  return (
    <div className="grid gap-4">
      <PageCard
        title={`Question ${activeIdx + 1} of ${items.length}`}
        subtitle={`${answeredCount} answered · ${attempt.total_marks} total marks · Closes in ${formatTimeUntil(attempt.expires_at)}`}
      >
        <QuestionRenderer
          item={activeItem}
          response={localResponses[activeItem.question_id] ?? activeItem.response}
          onChange={(value) =>
            setLocalResponses((prev) => ({ ...prev, [activeItem.question_id]: value }))
          }
        />
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-muted-foreground inline-flex items-center gap-2 text-xs">
            {saveMutation.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : saveMutation.isError ? (
              <>
                <AlertTriangle className="text-destructive h-3.5 w-3.5" />
                <span className="text-destructive">
                  Couldn't save: {parsePublicError(saveMutation.error).message}
                </span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Saved automatically
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => flushSaveAndNavigate(-1)}
              disabled={activeIdx === 0}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => flushSaveAndNavigate(1)}
              disabled={activeIdx === items.length - 1}
            >
              Next
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                flushSaveAndNavigate(0);
                setConfirmSubmit(true);
              }}
            >
              Submit assessment
              <Send className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </PageCard>

      <PageCard subtitle="Jump to a question. Filled circles are answered; outlined are pending.">
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, idx) => {
            const answered = localResponses[it.question_id] !== undefined || it.response !== null;
            const active = idx === activeIdx;
            return (
              <button
                key={it.question_id}
                type="button"
                onClick={() => {
                  flushSaveAndNavigate(0);
                  setActiveIdx(idx);
                }}
                className={`h-9 w-9 rounded-md border text-xs font-semibold transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : answered
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-border bg-card text-muted-foreground hover:border-foreground/30"
                }`}
                aria-label={`Go to question ${idx + 1}${answered ? ", answered" : ""}`}
                aria-current={active ? "step" : undefined}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>
      </PageCard>

      {confirmSubmit && (
        <SubmitConfirmation
          totalQuestions={items.length}
          answered={answeredCount}
          onConfirm={() => submitMutation.mutate()}
          onCancel={() => setConfirmSubmit(false)}
          submitting={submitMutation.isPending}
          submitError={submitMutation.error}
        />
      )}
    </div>
  );
}

function SubmitConfirmation({
  totalQuestions,
  answered,
  onConfirm,
  onCancel,
  submitting,
  submitError,
}: {
  totalQuestions: number;
  answered: number;
  onConfirm: () => void;
  onCancel: () => void;
  submitting: boolean;
  submitError: unknown;
}) {
  const errorMsg = submitError ? parsePublicError(submitError).message : null;
  const allAnswered = answered === totalQuestions;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24 }}
    >
      <PageCard
        title="Submit assessment?"
        subtitle="You won't be able to change your answers after submission."
      >
        <p className="text-foreground/90 text-sm">
          You have answered <span className="font-semibold">{answered}</span> of{" "}
          <span className="font-semibold">{totalQuestions}</span> questions.
          {!allAnswered && (
            <> Unanswered questions will be marked as zero. You can still cancel and go back.</>
          )}
        </p>
        {errorMsg && (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-destructive mt-3 flex items-start gap-2 rounded-md border p-3 text-sm"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Keep editing
          </Button>
          <Button type="button" onClick={onConfirm} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              "Submit final answers"
            )}
          </Button>
        </div>
      </PageCard>
    </motion.div>
  );
}

function SubmittedView({
  submit,
  attempt,
}: {
  submit: PublicSubmitAttemptResponse;
  attempt: PublicApplicantAttemptResponse;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.985, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.36, ease: [0.32, 0.72, 0.32, 1] }}
    >
      <PageCard
        title={
          <span className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <span>Assessment submitted</span>
          </span>
        }
        subtitle="Thank you for completing the assessment."
      >
        <p className="text-foreground/90 text-sm">
          We've recorded your answers and notified the institution.
          {submit.fully_auto_graded
            ? " Your result has been auto-graded and will appear on your application status page shortly."
            : " A staff member will review and grade your responses, and the result will appear on your application status page once it's ready."}
        </p>
        <p className="text-muted-foreground mt-3 text-xs">
          Submitted at: {formatDateTime(submit.submitted_at)} · Assessment:{" "}
          {assessmentLabel(attempt)}
        </p>
      </PageCard>
    </motion.div>
  );
}

function AlreadyDoneView({ attempt }: { attempt: PublicApplicantAttemptResponse }) {
  return (
    <PageCard
      title={
        <span className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <span>Assessment already submitted</span>
        </span>
      }
      subtitle="You've completed this assessment. The result will appear on your application status page once grading is finalised."
    >
      <p className="text-muted-foreground text-xs">
        Submitted at: {formatDateTime(attempt.submitted_at)} · Assessment:{" "}
        {assessmentLabel(attempt)}
      </p>
    </PageCard>
  );
}

function AttemptError({ err }: { err: unknown }) {
  const parsed = parsePublicError(err);
  if (parsed.code === "ATTEMPT_NOT_AVAILABLE") {
    return (
      <ErrorState
        title="No attempt available"
        body="There's no active assessment attempt for this link. Please contact the institution if you believe this is a mistake."
      />
    );
  }
  if (parsed.kind === "tenant_or_form_not_found") {
    return (
      <ErrorState
        title="Assessment not available"
        body="We couldn't find an active assessment for this link. The link may have been revoked or already used."
      />
    );
  }
  return (
    <ErrorState
      title="Couldn't load assessment"
      body={parsed.message}
      cta={<Button onClick={() => window.location.reload()}>Reload</Button>}
    />
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

/**
 * Stable JSON stringification — Object.keys() ordering is engine-defined
 * for non-numeric keys but `JSON.stringify` follows insertion order, so
 * `{a:1,b:2}` and `{b:2,a:1}` would serialise differently and trigger
 * spurious autosaves. Sort keys recursively so the equality check is
 * deterministic.
 */
function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = sortKeys((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

/**
 * The BE wire shape for `response` is `Record<string, unknown>`. Coerce
 * primitives / arrays into a `{ value: ... }` envelope so the wire is
 * always a JSON object — consistent with how the BE persists scalar
 * answers (it always wraps them).
 */
function toRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return { value };
}
